// Traffic target detail card — slide-over panel shown when the pilot
// taps an aircraft icon on the map. Mirrors the AirspaceDetail /
// AirportDetail pattern: positioned absolute, dismissible by tapping
// the close button or anywhere outside the card.
//
// Data shape comes from useTrafficStore (keyed on icaoHex). We read
// the LIVE row from the store so position / altitude / GS updates
// as the poller refreshes; the parent only passes the icaoHex.

import { useEffect } from 'react';
import { useTrafficStore } from '../lib/traffic/store';
import { useGpsStore } from '../stores/gpsStore';
import { greatCircleNm, bearingDeg } from '../lib/geo';
import { Icon } from '../shell/Icon';

interface Props {
  icaoHex: string;
  onClose: () => void;
}

export default function TrafficDetail({ icaoHex, onClose }: Props) {
  const target = useTrafficStore((s) => s.targets.get(icaoHex));
  const ownship = useGpsStore((s) => s.fix);

  // Close automatically if the target ages out of the store (e.g.
  // poller stops returning it for > freshness window).
  useEffect(() => {
    if (!target) onClose();
  }, [target, onClose]);

  if (!target) return null;

  const ownAltFt = ownship?.altMeters != null ? ownship.altMeters * 3.28084 : null;
  const deltaFt =
    ownAltFt != null && target.altBaroFt != null
      ? target.altBaroFt - ownAltFt
      : null;
  const distNm = ownship
    ? greatCircleNm(ownship.lat, ownship.lon, target.lat, target.lon)
    : null;
  const brgDeg = ownship
    ? bearingDeg(ownship.lat, ownship.lon, target.lat, target.lon)
    : null;
  const ageMs = Date.now() - target.lastSeenMs;
  const ageStr =
    ageMs < 2_000 ? 'live'
    : ageMs < 60_000 ? `${(ageMs / 1000).toFixed(0)}s ago`
    : `${Math.round(ageMs / 60_000)}m ago`;

  const sourceLabel =
    target.source === 'gdl90' ? 'GDL90 receiver'
    : target.source === 'connext' ? 'Garmin Connext'
    : 'adsb.fi (internet)';

  return (
    <div className="absolute inset-x-0 bottom-0 z-30 md:left-auto md:right-4 md:bottom-4 md:max-w-md">
      <div className="bg-cockpit-800 border border-cockpit-600 rounded-t-xl md:rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 p-3 border-b border-cockpit-700">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-cockpit-100 text-lg">
                {target.callsign || target.registration || target.icaoHex.toUpperCase()}
              </span>
              <SourceBadge source={target.source} />
              {target.emergency !== 'none' && (
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-amber-600 text-white">
                  {target.emergency}
                </span>
              )}
              {target.military && (
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-cockpit-600 text-cockpit-100">
                  MIL
                </span>
              )}
            </div>
            <div className="text-xs text-cockpit-300 mt-0.5 truncate">
              {[target.typeDescription, target.operator].filter(Boolean).join(' · ') || target.typeIcao || '—'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-1 rounded hover:bg-cockpit-700"
            aria-label="Close traffic detail"
          >
            <Icon name="x" className="h-4 w-4 text-cockpit-300" />
          </button>
        </div>

        {/* Body */}
        <div className="p-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
          <Stat
            label="Altitude"
            value={
              target.altBaroFt != null
                ? `${target.altBaroFt.toLocaleString()} ft${deltaFt != null ? ` (Δ ${deltaFt > 0 ? '+' : ''}${Math.round(deltaFt).toLocaleString()})` : ''}`
                : '—'
            }
          />
          <Stat
            label="Vertical"
            value={
              target.vertRateFpm != null
                ? `${target.vertRateFpm > 0 ? '+' : ''}${Math.round(target.vertRateFpm)} fpm`
                : '—'
            }
          />
          <Stat
            label="Groundspeed"
            value={target.groundspeedKt != null ? `${Math.round(target.groundspeedKt)} kt` : '—'}
          />
          <Stat
            label="Track"
            value={target.trackDeg != null ? `${Math.round(target.trackDeg).toString().padStart(3, '0')}°` : '—'}
          />
          <Stat
            label="Distance"
            value={distNm != null ? `${distNm.toFixed(1)} nm` : '—'}
          />
          <Stat
            label="Bearing"
            value={brgDeg != null && Number.isFinite(brgDeg) ? `${Math.round(brgDeg).toString().padStart(3, '0')}°` : '—'}
          />
          <Stat
            label="Squawk"
            value={target.squawk ? target.squawk : '—'}
          />
          <Stat
            label="Position"
            value={`${target.lat.toFixed(4)}, ${target.lon.toFixed(4)}`}
          />
        </div>

        {/* Footer */}
        <div className="px-3 pb-3 flex items-center justify-between text-[10px] text-cockpit-300 font-mono">
          <span>ICAO {target.icaoHex.toUpperCase()}</span>
          <span>Updated {ageStr}</span>
        </div>
        <div className="px-3 pb-3 text-[10px] text-cockpit-400 leading-snug">
          Source: {sourceLabel}.{target.source === 'internet' ? ' Position may lag actual aircraft by 5–15 sec. Do not use for separation.' : ''}{' '}Supplemental only.
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest text-cockpit-400">{label}</div>
      <div className="font-mono text-cockpit-100">{value}</div>
    </div>
  );
}

function SourceBadge({ source }: { source: 'internet' | 'gdl90' | 'connext' }) {
  const map = {
    internet: { label: 'INET', bg: 'bg-cockpit-600 text-cockpit-100' },
    gdl90:    { label: 'GDL90', bg: 'bg-emerald-600 text-white' },
    connext:  { label: 'CNXT', bg: 'bg-emerald-700 text-white' },
  } as const;
  const cfg = map[source];
  return (
    <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${cfg.bg}`}>
      {cfg.label}
    </span>
  );
}
